import { useQuery } from '@apollo/client';
import {
  BankIcon,
  FilesIcon,
  IdentificationBadgeIcon,
  InfoIcon,
  NotebookIcon,
  TreasureChestIcon,
} from '@phosphor-icons/react';
import { gql } from '__generated__/gql';
import { FC } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import bankIcon from 'media/icons/bank.svg';
import dematIcon from 'media/icons/demat-account.svg';
import documentsIcon from 'media/icons/documents.svg';
import investmentIcon from 'media/icons/investment.svg';

const INVESTMENT_ENTITY_QUERY = gql(`
  query getInvestmentEntity($id: ID!) {
    investmentEntity(id: $id) {
      id
      name
    }
  }
`);

const InvestmentEntityPage: FC = () => {
  const navigate = useNavigate();
  const { investmentEntityId } = useParams<{ investmentEntityId: string }>() as {
    investmentEntityId: string;
  };

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITY_QUERY, {
    variables: {
      id: investmentEntityId,
    },
  });

  const renderContent = () => {
    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const investmentEntity = data.investmentEntity;

    const tabs = [
      {
        key: 'information',
        label: 'Information',
        description: 'View and manage information',
        route: `/invest/investment-entities/${investmentEntityId}`,
        icon: InfoIcon,
        exact: true,
      },
      {
        key: 'kyc',
        label: 'KYC',
        description: 'Verify your identity',
        route: `/invest/investment-entities/${investmentEntityId}/kyc`,
        icon: IdentificationBadgeIcon,
      },
      {
        key: 'demat-account',
        label: 'Demat Account',
        description: 'View and manage demat account',
        route: `/invest/investment-entities/${investmentEntityId}/demat`,
        icon: NotebookIcon,
      },
      {
        key: 'bank-accounts',
        label: 'Bank Accounts',
        description: 'View and manage bank accounts',
        route: `/invest/investment-entities/${investmentEntityId}/bankAccounts`,
        icon: BankIcon,
      },
      {
        key: 'documents',
        label: 'Documents',
        description: 'View and manage documents',
        route: `/invest/investment-entities/${investmentEntityId}/documents`,
        icon: FilesIcon,
      },
      {
        key: 'investments',
        label: 'Investments',
        description: 'View and manage investments',
        route: `/invest/investment-entities/${investmentEntityId}/investments`,
        icon: TreasureChestIcon,
      },
      // Hidden for now; the route still exists.
      // {
      // key: 'unit-transfers',
      // label: 'Unit Transfers',
      // description: 'Transfer units or track a transfer',
      // route: `/invest/investment-entities/${investmentEntityId}/unit-transfers`,
      // icon: ArrowsLeftRightIcon,
      // },
    ];

    return (
      <>
        <SidebarLayoutNavigation
          onBackButtonClick={() => navigate(-1)}
          title={investmentEntity.name}
          subTitle="Investment Entity"
        />
        <SidebarLayoutContent secondaryTabs={tabs}>
          <Outlet />
        </SidebarLayoutContent>
      </>
    );
  };

  return <SidebarLayout>{renderContent()}</SidebarLayout>;
};

export default InvestmentEntityPage;
